import { useEffect, useId, useRef, useState } from "react";
import { ApiError } from "../../lib/api-client";
import { regenerateInvite } from "../../lib/api-family";
import type { Family } from "../../types/family";

interface InviteCardProps {
  family: Family;
  /** Whether the current user is an admin of this family. */
  isAdmin: boolean;
  /** Called with the updated family after the invite code is rotated. */
  onInviteCodeRotated: (newFamily: Family) => void;
}

const ROTATE_ERROR = "Не удалось обновить ссылку. Попробуйте ещё раз.";
const FORBIDDEN_ERROR = "Обновить код может только администратор семьи.";
const COPY_FEEDBACK_MS = 2000;

/** Returns true when the Web Share API is available in this environment. */
function canShare(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.share === "function";
}

/**
 * Copies text to the clipboard, falling back to a hidden textarea +
 * document.execCommand when the async Clipboard API is unavailable.
 */
async function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to the legacy path
    }
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

export function InviteCard({
  family,
  isAdmin,
  onInviteCodeRotated,
}: InviteCardProps) {
  const linkId = useId();
  const errorId = useId();

  const [isRotating, setIsRotating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmingRotate, setConfirmingRotate] = useState(false);

  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear any pending "Скопировано!" reset timer when the card unmounts so it
  // does not fire setState on an unmounted component.
  useEffect(() => {
    return () => {
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
      }
    };
  }, []);

  const inviteUrl = `${window.location.origin}/join?code=${family.invite_code}`;
  const shareSupported = canShare();

  async function handleCopy() {
    const ok = await copyToClipboard(inviteUrl);
    if (!ok) {
      setError("Не удалось скопировать ссылку.");
      return;
    }
    setError(null);
    setCopied(true);
    if (copyTimerRef.current) {
      clearTimeout(copyTimerRef.current);
    }
    copyTimerRef.current = setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
  }

  async function handleShare() {
    try {
      await navigator.share({
        title: "Приглашение в семью",
        url: inviteUrl,
      });
    } catch {
      // User cancelled the share sheet or it is unsupported — no-op.
    }
  }

  async function handleRotate() {
    setIsRotating(true);
    setError(null);
    setConfirmingRotate(false);

    try {
      const updated = await regenerateInvite(family.id);
      onInviteCodeRotated(updated);
    } catch (err) {
      if (err instanceof ApiError && err.code === "FORBIDDEN") {
        setError(FORBIDDEN_ERROR);
      } else {
        setError(ROTATE_ERROR);
      }
    } finally {
      setIsRotating(false);
    }
  }

  return (
    <section aria-labelledby={`${linkId}-heading`} className="mx-auto w-full max-w-3xl px-5 pt-5 lg:px-0 lg:pt-8">
      <h2
        id={`${linkId}-heading`}
        className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-action"
      >
        Приглашение в семью
      </h2>

      <div className="rounded-[24px] border border-black/[0.04] bg-white p-5 shadow-[0_12px_32px_rgba(35,33,25,0.06)]">
        <p className="mb-3 text-sm font-bold text-content">
          Ссылка-приглашение
        </p>

        {isRotating ? (
          <div className="flex items-center gap-3">
            <div
              aria-hidden="true"
              className="h-[44px] flex-1 animate-pulse rounded-lg bg-[#E5E0DB]"
            />
            <span
              role="status"
              aria-live="polite"
              className="sr-only"
            >
              Обновляем ссылку…
            </span>
            <span
              aria-hidden="true"
              className="block h-7 w-7 animate-spin rounded-full border-2 border-action border-t-transparent"
            />
          </div>
        ) : (
          <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
            <input
              id={linkId}
              type="text"
              readOnly
              value={inviteUrl}
              aria-label="Ссылка-приглашение"
              onFocus={(event) => event.currentTarget.select()}
              className="form-control h-[44px] min-w-0 w-full truncate rounded-lg border border-line bg-surface-subtle px-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-action/40 sm:flex-1"
            />
            <button
              type="button"
              onClick={handleCopy}
              className="h-[44px] w-full shrink-0 cursor-pointer whitespace-nowrap rounded-xl bg-[#171714] px-4 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 sm:w-auto"
            >
              {copied ? "Скопировано!" : "Скопировать"}
            </button>
          </div>
        )}

        {isRotating && (
          <p className="mt-3 text-sm text-muted">Обновляем ссылку…</p>
        )}

        {!isRotating && (
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            {shareSupported && (
              <button
                type="button"
                onClick={handleShare}
                className="h-[44px] flex-1 rounded-lg border border-line bg-white px-4 text-sm font-semibold text-content transition-colors hover:bg-surface-subtle focus:outline-none focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-2 focus-visible:ring-offset-white cursor-pointer"
              >
                Поделиться
              </button>
            )}

            {isAdmin && !confirmingRotate && (
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setConfirmingRotate(true);
                }}
                className="h-[44px] flex-1 rounded-lg border border-line bg-white px-4 text-sm font-semibold text-content transition-colors hover:bg-surface-subtle focus:outline-none focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-2 focus-visible:ring-offset-white cursor-pointer"
              >
                Обновить код
              </button>
            )}
          </div>
        )}

        {isAdmin && confirmingRotate && !isRotating && (
          <div className="mt-3 rounded-lg border border-line bg-surface-subtle p-3">
            <p className="text-sm text-muted">
              Старая ссылка перестанет работать. Обновить код?
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={handleRotate}
                className="h-[40px] flex-1 rounded-lg bg-action px-4 text-sm font-semibold text-white transition-colors hover:bg-action-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-2 focus-visible:ring-offset-surface-subtle cursor-pointer"
              >
                Обновить
              </button>
              <button
                type="button"
                onClick={() => setConfirmingRotate(false)}
                className="h-[40px] flex-1 rounded-lg border border-line bg-white px-4 text-sm font-semibold text-content transition-colors hover:bg-surface-subtle focus:outline-none focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-2 focus-visible:ring-offset-surface-subtle cursor-pointer"
              >
                Отмена
              </button>
            </div>
          </div>
        )}

        {error && (
          <div
            id={errorId}
            role="alert"
            className="mt-3 rounded-lg border-l-4 border-danger bg-danger-soft px-3 py-2.5 text-sm font-medium text-danger"
          >
            {error}
          </div>
        )}
      </div>

      {isAdmin && (
        <p className="mt-2 text-xs text-muted">
          Кнопка «Обновить код» видна только администратору.
        </p>
      )}
    </section>
  );
}
